import React, { useRef, useState } from 'react';

import {
  Button,
  Grid,
  Heading,
  Icon,
  Stack,
  Text,
  tv,
} from '@eluve/components';

import { ConversationHistory } from './ConversationChatPanel';
import { useChat } from './ConversationContextProvider';

const panelStyles = tv({
  base: 'relative z-0 size-full overflow-hidden text-clip select-none',
});

const backgroundStyles = tv({
  base: 'absolute top-0 left-0 z-20 bg-white bg-center bg-no-repeat transition-all duration-300 ease-in-out',
  variants: {
    isOpen: {
      true: 'h-2/3 w-full bg-contain',
      false: 'size-full scale-[1.72] bg-contain',
    },
  },
});

const chatContainerStyles = tv({
  base: 'absolute bottom-0 left-0 z-30 h-3/5 w-full gap-0 rounded-t-3xl bg-white pt-3 transition-transform duration-300 ease-in-out',
  variants: {
    isOpen: {
      true: 'translate-y-0',
      false: 'translate-y-full',
    },
  },
});

const providerBadgeStyles = tv({
  base: 'flex h-10 rounded-full bg-text-primary pr-3 pl-2 text-sm font-semibold text-white opacity-75',
});

const chatToggleButtonStyles = tv({
  base: 'flex size-12 rounded-full bg-text-primary text-white hover:bg-text-primary hover:opacity-75',
});

const micButtonStyles = tv({
  base: 'flex size-24 items-center justify-center rounded-full bg-text-primary text-white hover:bg-text-primary hover:opacity-75',
});

const stopButtonStyles = tv({
  base: 'flex size-12 rounded-full bg-text-primary text-white hover:bg-text-primary hover:opacity-75',
});

type ConversationActivePanelProps = {
  providerName?: string;
  providerAvatarUrl?: string;
  providerBackgroundUrl?: string;
};

export const ConversationActivePanel: React.FC<
  ConversationActivePanelProps
> = ({ providerName, providerAvatarUrl, providerBackgroundUrl }) => {
  const {
    isConnected,
    handlePushToTalk,
    openChat,
    setOpenChat,
    toggleConnection,
    setIsCompleted,
    isCompleted,
    appointmentConversationId,
  } = useChat();
  const [pushToTalk, setPushToTalk] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const startTalking = () => {
    handlePushToTalk(true);
    setPushToTalk(true);
  };

  const stopTalking = () => {
    if (!pushToTalk) return;
    setPushToTalk(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    handlePushToTalk(false);
  };

  const doneWithConversation = () => {
    toggleConnection(false, appointmentConversationId);
    setIsCompleted(true);
    return;
  };

  if (!isConnected || isCompleted) return null;

  return (
    <div className={panelStyles()}>
      <div
        style={{
          backgroundImage: `url(${providerBackgroundUrl ?? providerAvatarUrl})`,
        }}
        onClick={() => {
          setOpenChat(false);
        }}
        className={backgroundStyles({ isOpen: openChat })}
      />
      <Stack justify="between" gap={2} fullHeight>
        <Stack
          justify="between"
          className="z-30 p-6"
          gap={2}
          direction="horizontal"
        >
          <Stack
            fullWidth={false}
            className={providerBadgeStyles()}
            gap={2}
            direction="horizontal"
          >
            <img
              src={providerAvatarUrl ?? ''}
              alt="Dr"
              className="size-6 rounded-full bg-white"
            />
            {providerName}
          </Stack>

          <Button
            className={chatToggleButtonStyles()}
            disabled={!isConnected}
            onClick={() => {
              setOpenChat(!openChat);
            }}
          >
            <Icon name="MessageSquare" size="2xl" />
          </Button>
        </Stack>

        {!openChat && (
          <Stack className="z-30 pb-10" gap={2}>
            <Grid columns={3} justify="center" align="center">
              <div />
              <Stack
                justify="center"
                align="center"
                className={isConnected ? 'active:opacity-75' : ''}
                gap={2}
              >
                <Button
                  className={micButtonStyles()}
                  disabled={!isConnected}
                  onMouseDown={startTalking}
                  onMouseUp={stopTalking}
                  onMouseLeave={stopTalking}
                >
                  {pushToTalk ? (
                    <Icon
                      name="AudioWaveform"
                      size="2xl"
                      className="animate-pulse"
                    />
                  ) : (
                    <Icon name="Mic" size="2xl" />
                  )}
                </Button>
                <Text className="h-6">{pushToTalk ? '' : 'Hold to talk'}</Text>
              </Stack>
              <Stack
                className={isConnected ? 'active:opacity-75' : ''}
                align="start"
                gap={2}
              >
                <Button
                  className={stopButtonStyles()}
                  onClick={doneWithConversation}
                >
                  <Icon name="Square" size="2xl" className="fill-white" />
                </Button>

                <Text className="h-6"></Text>
              </Stack>
            </Grid>
            <Grid columns={3} justify="center" align="center" gap={8}></Grid>
          </Stack>
        )}
        <div className={chatContainerStyles({ isOpen: openChat })}>
          <Stack gap={3} className="p-2" justify="between" fullHeight>
            <Stack
              className="shrink-0"
              justify="between"
              gap={2}
              direction="horizontal"
            >
              <Heading size="xl" weight="semibold">
                Conversation
              </Heading>
              <Button
                variant="ghost"
                size="lg"
                onClick={() => setOpenChat(false)}
              >
                <Icon name="X" size="auto" />
              </Button>
            </Stack>
            <Stack className="overflow-auto p-2" gap={2} direction="horizontal">
              <ConversationHistory
                providerAvatarUrl={providerAvatarUrl}
                providerName={providerName}
              />
            </Stack>
          </Stack>
        </div>
      </Stack>
    </div>
  );
};
